import { useCallback, useEffect, useRef, useState } from "react"
import { StyleProvider } from "@ant-design/cssinjs"
import { safeRender, type RootEntry } from "../core/shadowPortal"
import { positionTopBarOverlay, resolveTopBarSideOverhang } from "../core/overlayContainers"
import { JumpingNavigationBar } from "../JumpingNavigationBar/JumpingNavigationBar"
import { getEcallsForErefId } from "../quotationViewer.helpers"
import AnnotationService, { SMART_NAV_MAX_ZOOM } from "services/annotations.service"
import { smartNavDebug } from "../smartNavDebug"
import { useSplitScreenStore } from "store/splitScreen.store"
import type { Dispatch, MutableRefObject, SetStateAction } from "react"
import type { TEcall, TErefWithFurniture, TFurniture, TPageElevationData } from "types/elevationReferences.type"
import type { TViewerPane } from "services/annotations.service"
import type { TRightPanelMode } from "../quotationViewer.helpers"

// Delay before re-placing a top bar, after the sidebar its session opens has finished widening.
const TOP_BAR_RESETTLE_MS = 350

// A "jump session" is the temporary navigation mode entered from a sidebar's "view connected"
// action: stepping through a furniture's connected callouts, an eref's callouts, or an ecall's
// elevation references, with a floating JumpingNavigationBar and a "back to source" return. The
// source entity + the ordered item list are held in state (mirrored to a ref so the canvas
// selection handler can read the live session without re-subscribing).
type TJumpSource =
	| { kind: "furniture"; furniture: TFurniture }
	| { kind: "eref"; eref: TErefWithFurniture }
	| { kind: "ecall"; ecall: TEcall }

// Which pane a session drives, and which pane its source lives in. Both are "primary" for every
// session started from a sidebar's "view connected" action — the single-viewer behaviour, unchanged.
// Smart navigation (#1311) is the one caller that splits them: it applies the items in the SECONDARY
// pane while the source stays selected in the Primary one, which is what puts the bar over the
// correct half of the screen.
type TJumpPanes = {
	pane: TViewerPane
	sourcePane: TViewerPane
	// Set by smart navigation so it can remember this session's position across Primary flips
	// (decision D8). Nothing else reads it.
	memoryKey?: string
	// Whether the session's items are AUTO-selected counterparts, added to the user's own selection
	// instead of replacing it (#1311) — see AnnotationService.selectEntityAsCounterpart. Only smart
	// navigation sets it; a session started from a sidebar's "view connected" action still replaces
	// the selection, exactly as before.
	additiveItems?: boolean
}
export type TJumpSession =
	| ({ itemKind: "ecall"; source: TJumpSource; items: TEcall[]; index: number } & TJumpPanes)
	| ({ itemKind: "eref"; source: TJumpSource; items: TErefWithFurniture[]; index: number } & TJumpPanes)

export type TJumpSessionOptions = Partial<TJumpPanes>


type TJumpNavigationCtx = {
	isModalOpen: boolean
	isDocumentLoaded: boolean
	/**
	 * True while another mode owns the document area (the connect-objects picker, REQ-372). The bar
	 * stays up — it keeps the top slot and the picker stacks below it — but its navigation is greyed
	 * out, because stepping the session would scroll away from the objects being picked and reset
	 * their selection. Its close button stays live.
	 */
	isNavigationDisabled: boolean
	relatedObjects: TPageElevationData[] | undefined
	allEcallsRef: MutableRefObject<TEcall[]>
	getShadowRoot: () => ShadowRoot | undefined
	openReferencePanel: () => void
	findErefWithFurnitureById: (erefId: number) => TErefWithFurniture | undefined
	// Selection state (owned by the orchestrator) that a jump target drives.
	setSelectedEref: Dispatch<SetStateAction<TErefWithFurniture | undefined>>
	selectedErefRef: MutableRefObject<TErefWithFurniture | undefined>
	setSelectedFurniture: Dispatch<SetStateAction<TFurniture | undefined>>
	selectedFurnitureRef: MutableRefObject<TFurniture | undefined>
	setSelectedEcall: Dispatch<SetStateAction<TEcall | undefined>>
	selectedEcallRef: MutableRefObject<TEcall | undefined>
	setRightPanelMode: Dispatch<SetStateAction<TRightPanelMode>>
	setFurnitureInitialTab: Dispatch<SetStateAction<"Details" | "Connected callouts">>
	setFurnitureMountEpoch: Dispatch<SetStateAction<number>>
	// Jumping-nav overlay (created by createOverlayContainers, owned by the orchestrator).
	jumpingNavEntryRef: MutableRefObject<RootEntry | null>
	jumpingNavDocAreaRef: MutableRefObject<HTMLElement | null>
	jumpingNavTargetContainerRef: MutableRefObject<HTMLElement | null>
	// The SECOND jumping-nav overlay, positioned over the split-screen pane (#1311). A session whose
	// `pane` is "split" renders here instead.
	splitJumpingNavEntryRef: MutableRefObject<RootEntry | null>
	// Live per-pane document-area lookup. The split pane's element is replaced whenever Apryse
	// re-mounts the document area, so it is resolved on every render rather than captured.
	getPaneDocAreaEl: (pane: TViewerPane) => HTMLElement | null
	// Open an entity in a given pane's sidebar (the orchestrator's own click handlers). Used for
	// sessions that drive a pane other than the primary one.
	openErefInPane: (eref: TErefWithFurniture, pane: TViewerPane, additive?: boolean) => void
	openEcallInPane: (ecall: TEcall, pane: TViewerPane, additive?: boolean) => void
	// Furniture is a jump source only (no counterpart direction), so this takes no `additive`.
	openFurnitureInPane: (furniture: TFurniture, pane: TViewerPane) => void
	// Close a pane's own detail sidebar. Used to take the Secondary pane's sidebar back down when a
	// smart-navigation session ends — see closeJumpSession.
	closePaneSidebar: (pane: TViewerPane) => void
	/**
	 * Run a programmatic selection attributed to a named pane, with smart navigation muted. The
	 * primary-pane branch below selects through AnnotationService directly, and Apryse's synchronous
	 * `annotationSelected` would otherwise be routed by whatever pane the OUTER caller last tagged —
	 * which, inside the split pane's mousedown, is "split", so the primary pane's target opened the
	 * split pane's sidebar as well and both sidebars showed the same entity.
	 */
	selectInPaneSilently: (pane: TViewerPane, select: () => void) => void
	/**
	 * Raised while THIS hook is navigating, so the selections it makes are not fed back to smart
	 * navigation as if the user had clicked them (#1311). Without it, stepping the session would look
	 * like a selection in the Secondary pane and flip the Primary role on every prev/next.
	 */
	suppressSmartNavRef: MutableRefObject<boolean>
}

export const useJumpNavigation = ({
	isModalOpen,
	isDocumentLoaded,
	isNavigationDisabled,
	relatedObjects,
	allEcallsRef,
	getShadowRoot,
	openReferencePanel,
	findErefWithFurnitureById,
	setSelectedEref,
	selectedErefRef,
	setSelectedFurniture,
	selectedFurnitureRef,
	setSelectedEcall,
	selectedEcallRef,
	setRightPanelMode,
	setFurnitureInitialTab,
	setFurnitureMountEpoch,
	jumpingNavEntryRef,
	jumpingNavDocAreaRef,
	jumpingNavTargetContainerRef,
	splitJumpingNavEntryRef,
	getPaneDocAreaEl,
	openErefInPane,
	openEcallInPane,
	openFurnitureInPane,
	closePaneSidebar,
	selectInPaneSilently,
	suppressSmartNavRef,
}: TJumpNavigationCtx) => {
	// Only used for the bar's compact (one-pane-wide) variant — see the render effect.
	const isSplitActive = useSplitScreenStore((state) => state.isSplitActive)
	// Pending re-place of the jumping bar after a sidebar width change (see the render effect).
	const topBarResettleTimerRef = useRef<number | null>(null)
	const isAutoOpenFloorPlan = useSplitScreenStore((state) => state.isAutoOpenFloorPlan)
	const primaryPane = useSplitScreenStore((state) => state.primaryPane)

	// Where a sidebar's "view connected" session should land.
	//
	// Before split screen there was one pane and the answer was always "primary", which is what these
	// handlers were written against. With smart navigation LIVE the counterpart belongs in the
	// SECONDARY pane, exactly as it does for a canvas selection — the Primary pane is where the user
	// is looking. Left at the old default, this control scrolled the pane the user had just clicked in
	// onto the counterpart and left both panes' sidebars showing the same kind of entity: reported as
	// "the ecall clicked on the left screen scrolls the LEFT screen to the connected eref" (#1311).
	//
	// `additiveItems` marks the session as smart-navigation-shaped, so the counterpart is ADDED to the
	// selection rather than replacing it and the Secondary sidebar is closed again when the session
	// ends. No `memoryKey`: the index memory belongs to sessions the machine itself started, keyed by
	// the selection that produced them.
	const resolveConnectedViewOptions = useCallback((): TJumpSessionOptions | undefined => {
		if (!isSplitActive || !isAutoOpenFloorPlan) return undefined
		return {
			pane: primaryPane === "primary" ? "split" : "primary",
			sourcePane: primaryPane,
			additiveItems: true,
		}
	}, [isSplitActive, isAutoOpenFloorPlan, primaryPane])
	const [jumpSession, setJumpSession] = useState<TJumpSession | null>(null)
	const jumpSessionRef = useRef<TJumpSession | null>(null)
	jumpSessionRef.current = jumpSession

	const findConnectedEcalls = useCallback(
		(furniture: TFurniture): TEcall[] => getEcallsForErefId(allEcallsRef.current, furniture.eref_id),
		[allEcallsRef],
	)

	// Apply the session's current item. A session that drives the SPLIT pane (#1311) goes through the
	// orchestrator's own pane-aware click handler instead of writing the primary selection state
	// directly — that is what routes the item into the right pane's sidebar and scrolls the right
	// pane rather than the left one.
	const applyJumpTargetInner = useCallback(
		(session: TJumpSession) => {
			const additive = session.additiveItems === true
			smartNavDebug("applyJumpTarget", {
				itemKind: session.itemKind,
				pane: session.pane,
				sourcePane: session.sourcePane,
				index: session.index,
				targetId: session.items[session.index]?.id,
				additive,
			})
			if (session.itemKind === "ecall") {
				const target = session.items[session.index]
				if (session.pane !== "primary") {
					openEcallInPane(target, session.pane, additive)
					return
				}
				selectedEcallRef.current = target
				setSelectedEcall(target)
				setRightPanelMode("ecall")
				AnnotationService.scrollToEcall(target.id, "primary", additive ? SMART_NAV_MAX_ZOOM : undefined)
				selectInPaneSilently("primary", () =>
					additive
						? AnnotationService.selectEntityAsCounterpart("ecall", target.id, "split")
						: AnnotationService.selectEcall(target.id),
				)
			} else {
				const target = session.items[session.index]
				if (session.pane !== "primary") {
					openErefInPane(target, session.pane, additive)
					return
				}
				selectedErefRef.current = target
				setSelectedEref(target)
				setRightPanelMode("eref")
				AnnotationService.scrollToEref(target.id, "primary", additive ? SMART_NAV_MAX_ZOOM : undefined)
				selectInPaneSilently("primary", () =>
					additive
						? AnnotationService.selectEntityAsCounterpart("eref", target.id, "split")
						: AnnotationService.selectEref(target.id),
				)
			}
		},
		[
			selectedEcallRef,
			setSelectedEcall,
			setRightPanelMode,
			selectedErefRef,
			setSelectedEref,
			openEcallInPane,
			openErefInPane,
			selectInPaneSilently,
		],
	)

	// Navigating a session is NOT a user selection in the pane it lands in: everything it selects is
	// bracketed so smart navigation ignores it and the Primary role stays where the user put it
	// (#1311). Without this, prev/next would hand Primary to the Secondary pane on every step.
	const applyJumpTarget = useCallback(
		(session: TJumpSession) => {
			suppressSmartNavRef.current = true
			try {
				applyJumpTargetInner(session)
			} finally {
				suppressSmartNavRef.current = false
			}
		},
		[applyJumpTargetInner, suppressSmartNavRef],
	)

	const startEcallJumpSession = useCallback(
		(source: TJumpSource, items: TEcall[], startIndex: number, options?: TJumpSessionOptions) => {
			if (items.length === 0) return
			const clamped = Math.max(0, Math.min(startIndex, items.length - 1))
			const next: TJumpSession = {
				itemKind: "ecall",
				source,
				items,
				index: clamped,
				pane: options?.pane ?? "primary",
				sourcePane: options?.sourcePane ?? "primary",
				memoryKey: options?.memoryKey,
				additiveItems: options?.additiveItems,
			}
			jumpSessionRef.current = next
			setJumpSession(next)
			// The panel to open is the one the ITEMS land in; a split-pane session's sidebar is opened
			// by the pane-aware click handler applyJumpTarget delegates to.
			if (next.pane === "primary") openReferencePanel()
			applyJumpTarget(next)
		},
		[applyJumpTarget, openReferencePanel],
	)

	const startErefJumpSession = useCallback(
		(source: TJumpSource, items: TErefWithFurniture[], startIndex: number, options?: TJumpSessionOptions) => {
			if (items.length === 0) return
			const clamped = Math.max(0, Math.min(startIndex, items.length - 1))
			const next: TJumpSession = {
				itemKind: "eref",
				source,
				items,
				index: clamped,
				pane: options?.pane ?? "primary",
				sourcePane: options?.sourcePane ?? "primary",
				memoryKey: options?.memoryKey,
				additiveItems: options?.additiveItems,
			}
			jumpSessionRef.current = next
			setJumpSession(next)
			if (next.pane === "primary") openReferencePanel()
			applyJumpTarget(next)
		},
		[applyJumpTarget, openReferencePanel],
	)

	const stepJumpSession = useCallback(
		(delta: number) => {
			const session = jumpSessionRef.current
			if (!session) return
			const nextIndex = Math.max(0, Math.min(session.index + delta, session.items.length - 1))
			if (nextIndex === session.index) return
			const next: TJumpSession = { ...session, index: nextIndex }
			jumpSessionRef.current = next
			setJumpSession(next)
			applyJumpTarget(next)
		},
		[applyJumpTarget],
	)

	const jumpPrev = useCallback(() => stepJumpSession(-1), [stepJumpSession])
	const jumpNext = useCallback(() => stepJumpSession(1), [stepJumpSession])

	const closeJumpSession = useCallback(() => {
		const session = jumpSessionRef.current
		if (session?.additiveItems === true) {
			AnnotationService.releaseCounterpartSelection()
			// A smart-navigation session (#1311) owns the Secondary pane's sidebar: it opened it to
			// show the counterpart. Ending the session has to close it again, or it stays on screen
			// describing an entity nothing is selecting — measured on every exit path (Escape, the
			// Auto toggle, an eref with no callouts, selecting a furniture). Sessions from a sidebar's
			// own "view connected" action are not additive and keep their sidebar, which is the whole
			// point of that affordance.
			closePaneSidebar(session.pane)
		}
		setJumpSession(null)
	}, [closePaneSidebar])

	const backToJumpSourceInner = useCallback(() => {
		const session = jumpSessionRef.current
		if (!session) return
		setJumpSession(null)
		// A session whose source lives in the split pane (#1311, extended to furniture per PR #1347
		// review) returns THERE, through the same pane-aware handlers applyJumpTarget uses.
		// The source is about to become the only selection again — release whatever the session had
		// auto-selected as its counterpart, so it neither lingers nor stays move-locked.
		AnnotationService.releaseCounterpartSelection()
		if (session.sourcePane !== "primary") {
			if (session.source.kind === "eref") openErefInPane(session.source.eref, session.sourcePane)
			else if (session.source.kind === "ecall") openEcallInPane(session.source.ecall, session.sourcePane)
			else openFurnitureInPane(session.source.furniture, session.sourcePane)
			return
		}
		if (session.source.kind === "furniture") {
			const source = session.source.furniture
			setFurnitureInitialTab("Connected callouts")
			setFurnitureMountEpoch((e) => e + 1)
			selectedFurnitureRef.current = source
			setSelectedFurniture(source)
			setRightPanelMode("furniture")
			openReferencePanel()
			AnnotationService.scrollToFurniture(source.id)
			AnnotationService.selectFurniture(source.id)
		} else if (session.source.kind === "eref") {
			const source = session.source.eref
			selectedErefRef.current = source
			setSelectedEref(source)
			setRightPanelMode("eref")
			openReferencePanel()
			AnnotationService.scrollToEref(source.id)
			AnnotationService.selectEref(source.id)
		} else {
			const source = session.source.ecall
			selectedEcallRef.current = source
			setSelectedEcall(source)
			setRightPanelMode("ecall")
			openReferencePanel()
			AnnotationService.scrollToEcall(source.id)
			AnnotationService.selectEcall(source.id)
		}
	}, [
		openReferencePanel,
		setFurnitureInitialTab,
		setFurnitureMountEpoch,
		selectedFurnitureRef,
		setSelectedFurniture,
		setRightPanelMode,
		selectedErefRef,
		setSelectedEref,
		selectedEcallRef,
		setSelectedEcall,
		openErefInPane,
		openEcallInPane,
		openFurnitureInPane,
	])

	// Returning to the source is a navigation too, not a fresh selection — suppressed for the same
	// reason as applyJumpTarget, so "back" ends the session instead of immediately rebuilding it.
	const backToJumpSource = useCallback(() => {
		suppressSmartNavRef.current = true
		try {
			backToJumpSourceInner()
		} finally {
			suppressSmartNavRef.current = false
		}
	}, [backToJumpSourceInner, suppressSmartNavRef])

	const handleConnectedCalloutView = useCallback(
		(ecall: TEcall) => {
			const furniture = selectedFurnitureRef.current
			if (!furniture) return
			const connected = findConnectedEcalls(furniture)
			if (connected.length === 0) return
			const idx = connected.findIndex((e) => e.id === ecall.id)
			// Same hand-off as handleErefCalloutView/handleEcallErefView below (PR #1347 review): with
			// smart navigation live, the callout belongs in the Secondary pane, not wherever the
			// furniture sidebar happens to be open.
			startEcallJumpSession(
				{ kind: "furniture", furniture },
				connected,
				idx >= 0 ? idx : 0,
				resolveConnectedViewOptions(),
			)
		},
		[findConnectedEcalls, startEcallJumpSession, selectedFurnitureRef, resolveConnectedViewOptions],
	)

	const handleErefCalloutView = useCallback(
		(ecall: TEcall) => {
			const eref = selectedErefRef.current
			if (!eref) return
			const connected = getEcallsForErefId(allEcallsRef.current, eref.id)
			if (connected.length === 0) return
			const idx = connected.findIndex((e) => e.id === ecall.id)
			// Same handoff as handleEcallErefView below (and handleConnectedCalloutView above, for
			// furniture): with smart navigation live the callouts belong in the Secondary pane.
			startEcallJumpSession({ kind: "eref", eref }, connected, idx >= 0 ? idx : 0, resolveConnectedViewOptions())
		},
		[startEcallJumpSession, selectedErefRef, allEcallsRef, resolveConnectedViewOptions],
	)

	const handleEcallErefView = useCallback(
		(erefId: number) => {
			const ecall = selectedEcallRef.current
			if (!ecall) return
			const connectedErefIdList = Array.from(
				new Set(ecall.elevation_numbers.map((en) => en.eref_id).filter((id): id is number => id != null)),
			)
			if (connectedErefIdList.length === 0) return
			const erefs = connectedErefIdList
				.map((id) => findErefWithFurnitureById(id))
				.filter((e): e is TErefWithFurniture => e != null)
			if (erefs.length === 0) return
			const idx = erefs.findIndex((e) => e.id === erefId)
			startErefJumpSession({ kind: "ecall", ecall }, erefs, idx >= 0 ? idx : 0, resolveConnectedViewOptions())
		},
		[findErefWithFurnitureById, startErefJumpSession, selectedEcallRef, resolveConnectedViewOptions],
	)

	// Render the floating jumping-nav bar into the overlay of the pane the session drives, positioned
	// across THAT pane's document area. Exactly one of the two overlays is ever visible: the other is
	// hidden and cleared on every pass, so a Primary flip moves the bar rather than leaving a second
	// one behind.
	useEffect(() => {
		const isSplitPaneSession = jumpSession?.pane === "split"
		const entryRef = isSplitPaneSession ? splitJumpingNavEntryRef : jumpingNavEntryRef
		const idleEntryRef = isSplitPaneSession ? jumpingNavEntryRef : splitJumpingNavEntryRef
		const entry = entryRef.current

		const idleEntry = idleEntryRef.current
		if (idleEntry) {
			idleEntry.container.style.display = "none"
			safeRender(idleEntryRef, null)
		}
		if (!entry) return

		const visible = isDocumentLoaded && jumpSession != null && jumpSession.items.length > 0
		entry.container.style.display = visible ? "block" : "none"

		if (visible) {
			// Resolved live, with the orchestrator's cached primary element only as a fallback: both
			// pane elements are replaced whenever Apryse re-mounts the document area.
			const docAreaEl = isSplitPaneSession
				? getPaneDocAreaEl("split")
				: (getPaneDocAreaEl("primary") ?? jumpingNavDocAreaRef.current)
			// Same rule `reposition` applies, from the same helper — see resolveTopBarSideOverhang.
			// Keyed on a LIVE split pane rather than on the store's flag so the two agree even while
			// the second viewer is still coming up.
			const place = () =>
				positionTopBarOverlay(
					entry.container,
					docAreaEl,
					jumpingNavTargetContainerRef.current,
					0,
					resolveTopBarSideOverhang(getPaneDocAreaEl("split") !== null),
				)
			place()
			// Opening a session also opens a sidebar, and the right panel WIDENS (291 → 320px) as it
			// does. Positioning once captures the pre-widening document area, so the bar keeps a width
			// it no longer fits — and with its gutter overhang on top, it ended up 28px inside the
			// sidebar. Re-place once the panel has settled; `place` reads the rects fresh each time.
			requestAnimationFrame(place)
			if (topBarResettleTimerRef.current != null) window.clearTimeout(topBarResettleTimerRef.current)
			topBarResettleTimerRef.current = window.setTimeout(place, TOP_BAR_RESETTLE_MS)
		}

		const shadowRoot = getShadowRoot()
		if (!shadowRoot) return

		if (!visible || !jumpSession) {
			safeRender(entryRef, null)
			return
		}

		const source = jumpSession.source
		let title: string
		if (source.kind === "furniture") {
			const furnitureLabel =
				(source.furniture.selected_model_number?.model_number && source.furniture.category?.name) || null
			title = furnitureLabel ? `Object ${furnitureLabel}` : "Object"
		} else if (source.kind === "eref") {
			const elevationNumber = source.eref.elevation_number
			const pageName = source.eref.page_name
			const base = elevationNumber ? `Elevation ${elevationNumber}` : "Elevation reference"
			title = pageName ? `${base} — ${pageName}` : base
		} else {
			const roomName = source.ecall.room_name
			title = roomName ? `Callout ${roomName}` : "Elevation callout"
		}

		safeRender(
			entryRef,
			<StyleProvider container={shadowRoot}>
				<JumpingNavigationBar
					title={title}
					currentIndex={jumpSession.index}
					total={jumpSession.items.length}
					isNavigationDisabled={isNavigationDisabled}
					// Both panes are roughly half-width while split screen is open, so the bar drops its
					// source title in either of them — not only in the split one.
					isCompact={isSplitActive}
					itemNoun={jumpSession.itemKind === "eref" ? "elevation reference" : "callout"}
					onBack={backToJumpSource}
					onPrev={jumpPrev}
					onNext={jumpNext}
					onClose={closeJumpSession}
				/>
			</StyleProvider>,
		)
	}, [
		getShadowRoot,
		isDocumentLoaded,
		isNavigationDisabled,
		isSplitActive,
		jumpSession,
		backToJumpSource,
		jumpPrev,
		jumpNext,
		closeJumpSession,
		jumpingNavEntryRef,
		jumpingNavDocAreaRef,
		jumpingNavTargetContainerRef,
		splitJumpingNavEntryRef,
		getPaneDocAreaEl,
	])

	// After a refetch, re-resolve the session's source + items from the fresh data so the bar keeps
	// pointing at the same entities (or closes if they're gone).
	useEffect(() => {
		const session = jumpSessionRef.current
		if (!session || !relatedObjects) return

		let nextSource: TJumpSource | undefined
		if (session.source.kind === "furniture") {
			const furnitureId = session.source.furniture.id
			for (const page of relatedObjects) {
				const found = page.furniture.find((f) => f.id === furnitureId)
				if (found) {
					nextSource = { kind: "furniture", furniture: found }
					break
				}
			}
		} else if (session.source.kind === "eref") {
			const erefId = session.source.eref.id
			for (const page of relatedObjects) {
				const eref = page.erefs.find((e) => e.id === erefId)
				if (eref) {
					const furniture = page.furniture.filter((f) => f.eref_id === erefId)
					nextSource = { kind: "eref", eref: { ...eref, furniture } }
					break
				}
			}
		} else {
			const ecallId = session.source.ecall.id
			const found = allEcallsRef.current.find((e) => e.id === ecallId)
			if (found) nextSource = { kind: "ecall", ecall: found }
		}
		if (!nextSource) {
			setJumpSession(null)
			return
		}

		const sourceEntity = (s: TJumpSource) =>
			s.kind === "furniture" ? s.furniture : s.kind === "eref" ? s.eref : s.ecall

		if (session.itemKind === "ecall") {
			const sourceErefId =
				nextSource.kind === "furniture"
					? nextSource.furniture.eref_id
					: nextSource.kind === "eref"
						? nextSource.eref.id
						: null
			if (sourceErefId == null) {
				setJumpSession(null)
				return
			}
			const nextItems = getEcallsForErefId(allEcallsRef.current, sourceErefId)
			if (nextItems.length === 0) {
				setJumpSession(null)
				return
			}
			const currentId = session.items[session.index]?.id
			const nextIndex = Math.max(
				0,
				nextItems.findIndex((e) => e.id === currentId),
			)
			const clampedIndex = Math.min(nextIndex, nextItems.length - 1)
			const changed =
				nextItems.length !== session.items.length ||
				clampedIndex !== session.index ||
				sourceEntity(nextSource) !== sourceEntity(session.source) ||
				nextItems.some((e, i) => e.id !== session.items[i]?.id)
			if (changed) {
				// The pane roles + memory key are properties of the SESSION, not of the data — carry them
				// through the re-resolve or the bar would jump back to the primary pane on every refetch.
				setJumpSession({
					itemKind: "ecall",
					source: nextSource,
					items: nextItems,
					index: clampedIndex,
					pane: session.pane,
					sourcePane: session.sourcePane,
					memoryKey: session.memoryKey,
					additiveItems: session.additiveItems,
				})
			}
			return
		}

		if (nextSource.kind !== "ecall") {
			setJumpSession(null)
			return
		}
		const connectedErefIdList = Array.from(
			new Set(nextSource.ecall.elevation_numbers.map((en) => en.eref_id).filter((id): id is number => id != null)),
		)
		const nextErefItems = connectedErefIdList
			.map((id) => findErefWithFurnitureById(id))
			.filter((e): e is TErefWithFurniture => e != null)
		if (nextErefItems.length === 0) {
			setJumpSession(null)
			return
		}
		const currentErefId = session.items[session.index]?.id
		const nextErefIndex = Math.max(
			0,
			nextErefItems.findIndex((e) => e.id === currentErefId),
		)
		const clampedErefIndex = Math.min(nextErefIndex, nextErefItems.length - 1)
		const erefChanged =
			nextErefItems.length !== session.items.length ||
			clampedErefIndex !== session.index ||
			sourceEntity(nextSource) !== sourceEntity(session.source) ||
			nextErefItems.some((e, i) => e.id !== session.items[i]?.id)
		if (erefChanged) {
			setJumpSession({
				itemKind: "eref",
				source: nextSource,
				items: nextErefItems,
				index: clampedErefIndex,
				pane: session.pane,
				sourcePane: session.sourcePane,
				memoryKey: session.memoryKey,
				additiveItems: session.additiveItems,
			})
		}
	}, [relatedObjects, findErefWithFurnitureById, allEcallsRef])

	// Drop the session when the modal closes (the viewer instance is reused across quotations).
	useEffect(() => {
		if (!isModalOpen) setJumpSession(null)
	}, [isModalOpen])

	return {
		jumpSession,
		jumpSessionRef,
		closeJumpSession,
		handleConnectedCalloutView,
		handleErefCalloutView,
		handleEcallErefView,
		// Exposed for useSmartNavigation (#1311), which starts sessions targeted at the SECONDARY pane.
		startEcallJumpSession,
		startErefJumpSession,
	}
}
